import { Box, Chip, Paper, Typography } from "@mui/material";
import InfoField from "../../Common/InfoField/InfoField";
import { SectionHeading, LabelText } from "../../Common/Typography/AppTypography";
import { getFormatedJSON } from "../../../utils/general";
import { emailSectionCardSx } from "../emailFormStyles";
import { mapNotificationTask } from "./notificationStatisticsMapper";

const statusChipColor = {
  success: "success",
  info: "info",
  primary: "primary",
  error: "error",
  warning: "warning",
  default: "default",
};

export const NotificationTaskStatusChip = ({ statusLabel, statusTone }) => (
  <Chip
    size="small"
    label={statusLabel || "-"}
    color={statusChipColor[statusTone] || "default"}
    variant={statusTone === "default" ? "outlined" : "filled"}
  />
);

const StatisticCard = ({ label, value }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.5,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      minWidth: 0,
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="h6" sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 600, lineHeight: 1.2 }}>
      {value}
    </Typography>
  </Paper>
);

const StatisticGroup = ({ title, items }) => {
  if (!items?.length) return null;
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        {items.map((item) => (
          <StatisticCard key={item.id} label={item.label} value={item.value} />
        ))}
      </Box>
    </Box>
  );
};

const NotificationTaskDetails = ({ task, rawTask }) => {
  const mapped = task || mapNotificationTask(rawTask || {});
  const hasStatistics =
    Boolean(mapped.statistics?.user?.length) || Boolean(mapped.statistics?.notification?.length);

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <SectionHeading sx={{ mt: 0, mb: 1.5 }}>General Information</SectionHeading>
      <Paper sx={{ ...emailSectionCardSx, mt: 0 }}>
        <InfoField label="Task ID" compact>
          <Box
            sx={{
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              fontSize: { xs: 13, sm: 14 },
              fontWeight: 600,
              whiteSpace: "normal",
              wordBreak: "break-all",
              lineHeight: 1.45,
            }}
          >
            {mapped.taskId || "-"}
          </Box>
        </InfoField>
        <InfoField label="Channel" value={mapped.channelLabel || "-"} compact />
        <InfoField label="Created At" value={mapped.timestampLabel || "-"} compact />
        <InfoField label="Status" compact>
          <NotificationTaskStatusChip
            statusLabel={mapped.statusLabel}
            statusTone={mapped.statusTone}
          />
        </InfoField>
      </Paper>

      {hasStatistics ? (
        <>
          <SectionHeading sx={{ mt: 2.5, mb: 0.5 }}>Statistics</SectionHeading>
          <StatisticGroup title="User Statistics" items={mapped.statistics.user} />
          <StatisticGroup title="Notification Statistics" items={mapped.statistics.notification} />
        </>
      ) : null}

      {mapped.message && Object.keys(mapped.message).length > 0 ? (
        <Box sx={{ mt: 2.5 }}>
          <LabelText sx={{ fontWeight: 400, color: "text.secondary", mb: 0.5 }}>
            Message
          </LabelText>
          <Box>{getFormatedJSON(mapped.message)}</Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default NotificationTaskDetails;
